import React, { useEffect, useState } from 'react';
import { auth } from './firebase';
import { signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import toast, { Toaster } from 'react-hot-toast';
import { Sidebar } from './components/layout/Sidebar';
import { LiveOperationsPage } from './pages/LiveOperationsPage';
import { DriversPage } from './pages/DriversPage';
import { RidersPage } from './pages/RidersPage';
import { RidesPage } from './pages/RidesPage';
import { OwnersPage, PaymentsPage, FraudPage, AnalyticsPage, SystemMonitoringPage, SupportPage, SettingsPage, ApplicationsPage } from './pages/Placeholders';

function Dashboard() {
  const [activeTab, setActiveTab] = useState('live');
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        try {
          const tokenResult = await u.getIdTokenResult();
          setIsAdmin(tokenResult.claims.admin === true);
        } catch (err) {
          console.error(err);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  if (loading) return <div className="flex items-center justify-center h-screen text-white">Loading...</div>;
  if (!user) return <LoginScreen />;
  if (!isAdmin) return <div className="flex items-center justify-center h-screen text-white bg-gray-950">Access denied. You are not an admin.</div>;

  const renderPage = () => {
    switch (activeTab) {
      case 'live': return <LiveOperationsPage />;
      case 'drivers': return <DriversPage />;
      case 'riders': return <RidersPage />;
      case 'rides': return <RidesPage />;
      case 'owners': return <OwnersPage />;
      case 'payments': return <PaymentsPage />;
      case 'fraud': return <FraudPage />;
      case 'analytics': return <AnalyticsPage />;
      case 'monitoring': return <SystemMonitoringPage />;
      case 'support': return <SupportPage />;
      case 'applications': return <ApplicationsPage />;
      case 'settings': return <SettingsPage />;
      default: return <LiveOperationsPage />;
    }
  };

  return (
    <div className="flex h-screen bg-gray-950 text-white">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
      <main className="flex-1 overflow-auto p-8">
        <Toaster position="top-right" />
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-limani-primary">Dashboard</h1>
          <p className="text-gray-400 text-sm mt-1">Real-time operations overview</p>
        </div>
        {renderPage()}
      </main>
    </div>
  );
}

function LoginScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      toast.success('Logged in successfully');
    } catch (err) {
      let errorMsg = err.message;
      if (err.code === 'auth/user-not-found') errorMsg = 'User not found. Please check your email.';
      else if (err.code === 'auth/wrong-password') errorMsg = 'Incorrect password.';
      else if (err.code === 'auth/too-many-requests') errorMsg = 'Too many failed attempts. Try later.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-950">
      <div className="bg-gray-900 p-8 rounded-2xl shadow-2xl w-96 border border-gray-800">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-limani-primary">Limani</h2>
          <p className="text-gray-400 text-sm">Operations Dashboard</p>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-1">Email Address</label>
            <input id="email" name="email" type="email" className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg" value={email} onChange={e => setEmail(e.target.value)} required />
          </div>
          <div className="mb-6">
            <label htmlFor="password" className="block text-sm font-medium text-gray-400 mb-1">Password</label>
            <input id="password" name="password" type="password" className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg" value={password} onChange={e => setPassword(e.target.value)} required />
          </div>
          <button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-500 py-3 rounded-lg font-semibold transition disabled:opacity-50">
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Dashboard;
